import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { CardNumberElement } from '@stripe/react-stripe-js';

import DashboardView from 'views/Administration/BuyCredit/Dashboard';
import PaymentService from 'api/Payment';
import AccountService from 'api/Account';
import UserService from 'api/User';
import SettingsActions from 'store/reducers/Settings';
import AccountActions from 'store/reducers/Account';
import UserActions from 'store/reducers/User';
import { useFetchTableData, useFetch } from 'hooks';
import { handlePromise } from 'utils';

const Dashboard = ( {
  parentPath,
  account,
  updateAccountInfo,
  updateUser,
  toggleErrorAlert,
  toggleLoading,
  user,
} ) => {
  const [dataSubmitted, setDataSubmitted] = useState( new Date().valueOf() );
  const [accountLoading, setAccountLoading] = useState( false );

  const [accountData] = useFetch( {
    initialState: {},
    promise: AccountService.getMyAccount,
    toggleErrorAlert,
  } );

  const [fetchData, data, dataLoading] = useFetchTableData( {
    promise: ( params ) => PaymentService.getPayments( {
      ...params,
      'filters[typeIn]': ['credit', 'plan'],
    } ),
    toggleErrorAlert,
    deps: [dataSubmitted],
  } );

  const reloadData = useCallback( () => {
    setDataSubmitted( new Date().valueOf() );
  }, [] );

  const reloadAccount = useCallback( async () => {
    setAccountLoading( true );
    const [, accountResponse, accountResponseData] = await handlePromise(
      AccountService.getMyAccount(),
    );
    if ( !accountResponse.ok ) return setAccountLoading( false );
    updateAccountInfo( accountResponseData );

    const [, response, responseData] = await handlePromise(
      UserService.getUser(),
    );
    setAccountLoading( false );
    if ( response.ok ) {
      updateUser( responseData );
    }
  }, [updateAccountInfo, updateUser] );

  const submitForm = useCallback(
    async ( paymentData, stripe, elements, callback ) => {
      if ( parseInt( paymentData.total, 10 ) ) {
        toggleLoading( true );

        const [errors, response, responseData] = await handlePromise(
          PaymentService.buyCredit( {
            amount: parseInt( paymentData.total, 10 ),
            iva: parseInt( paymentData.iva, 10 ),
            type: 'credit',
          } ),
        );
        if ( !response.ok ) {
          toggleLoading( false );
          return errors;
        }

        const result = await stripe.confirmCardPayment(
          responseData.client_secret,
          {
            payment_method: {
              card: elements.getElement( CardNumberElement ),
              billing_details: {
                name: account.fiscalName || account.name,
                address: {
                  city: account.city,
                  country: account.country,
                  postal_code: account.postalCode,
                  line1: account.address,
                },
              },
            },
          },
        );

        if ( !result ) return 'apiError';

        if ( result.error ) {
          if ( !result.error.payment_intent ) {
            toggleLoading( false );
            return result.error.message || 'apiError';
          }

          const [updateErrors, updateResponse] = await handlePromise(
            PaymentService.updateBuyCredit( responseData.id, {
              data: JSON.stringify( result.error.payment_intent ),
            } ),
          );
          toggleLoading( false );
          if ( result.error.message ) return result.error.message;
          if ( !updateResponse.ok ) return updateErrors;
          return 'apiError';
        }
        if ( result.paymentIntent.status === 'succeeded' ) {
          const [updateErrors, updateResponse] = await handlePromise(
            PaymentService.updateBuyCredit( responseData.id, {
              data: JSON.stringify( result.paymentIntent ),
            } ),
          );

          toggleLoading( false );
          if ( !updateResponse.ok ) return updateErrors;
          callback();
        }
      }
    },
    [account, toggleLoading],
  );

  const downloadInvoice = useCallback(
    async ( id ) => {
      const [errors, response, responseData] = await handlePromise(
        PaymentService.getInvoice( id ),
      );
      if ( !response.ok ) return toggleErrorAlert( errors );
      window.open( responseData.uri );
    },
    [toggleErrorAlert],
  );

  return (
    <DashboardView
      data={data}
      accountData={accountData}
      isLoading={dataLoading && accountLoading}
      parentPath={parentPath}
      onFetchData={fetchData}
      onBuyCredit={submitForm}
      onReload={reloadData}
      onReloadAccount={reloadAccount}
      onDownloadInvoice={downloadInvoice}
      userData={user}
    />
  );
};

const mapStateToProps = ( { account, user, } ) => ( {
  account,
  user,
} );

const mapDispatchToProps = {
  toggleErrorAlert: SettingsActions.toggleErrorAlert,
  toggleLoading: SettingsActions.toggleLoading,
  updateAccountInfo: AccountActions.updateInfo,
  updateUser: UserActions.updateUser,
};

export default connect( mapStateToProps, mapDispatchToProps )( Dashboard );
